import React from 'react';
import { Modal, Box, Typography, IconButton, Grid } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteIcon from '@mui/icons-material/Delete';
import api from './api';

// Style for the modal background
const style = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 1000, // Made slightly wider for the scattered effect
  bgcolor: '#f0f0f0', // Light grey background makes white photos pop
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
  maxHeight: '90vh',
  overflowY: 'auto'
};

function PhotoGallery({ open, onClose, photos, displayName, onPhotoDeleted }) {

  const handleDelete = async (photoId) => {
    if (!window.confirm('Are you sure you want to delete this photo?')) {
      return;
    }

    try {
      await api.delete(`/photos/${photoId}`);
      if (onPhotoDeleted) {
        onPhotoDeleted(photoId);
      }
    } catch (err) {
      console.error('Error deleting photo', err);
      alert('Failed to delete photo.');
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={style}>
        {/* Close Button */}
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8, zIndex: 20 }}
        >
          <CloseIcon />
        </IconButton>

        <Typography variant="h5" component="h2" gutterBottom sx={{ textAlign: 'center', fontFamily: 'cursive', mb: 4 }}>
          Memories from {displayName}
        </Typography>
        
        {photos.length === 0 ? (
          <Typography sx={{ textAlign: 'center' }}>No photos have been uploaded for this display yet.</Typography>
        ) : (
          <Grid container spacing={6} sx={{ p: 2 }}>
            {photos.map((photo, index) => (
              <Grid item key={photo.id} xs={12} sm={6} md={4}>
                
                {/* --- THE POLAROID CONTAINER --- */}
                <Box sx={{
                  backgroundColor: 'white',
                  padding: '12px 12px 50px 12px', // Big bottom padding for the "caption" area
                  boxShadow: '3px 3px 10px rgba(0,0,0,0.3)', 
                  // Rotate every second photo slightly for that "scattered" look
                  transform: `rotate(${index % 2 === 0 ? '3deg' : '-3deg'})`, 
                  transition: 'transform 0.3s ease, z-index 0.3s',
                  position: 'relative',
                  '&:hover': {
                    transform: 'scale(1.1) rotate(0deg)', // Pop up and straighten on hover
                    zIndex: 10, // Bring to front
                    boxShadow: '10px 10px 20px rgba(0,0,0,0.4)'
                  }
                }}>
                  
                  {/* The Image */}
                  
                  <Box
                    component="img"
                    src={photo.photo_url}
                    alt={`Photo of ${displayName}`}
                    sx={{
                      width: '100%',
                      height: '250px',
                      objectFit: 'cover', // Ensures the photo fits the square box
                      border: '1px solid #eee',
                      filter: 'sepia(0.2) contrast(1.1)' // Subtle vintage filter
                    }}
                  />

                  {/* The "Handwritten" Caption */}
                  <Typography 
                    sx={{ 
                      fontFamily: '"Comic Sans MS", "Chalkboard SE", "Marker Felt", sans-serif', 
                      textAlign: 'center',
                      mt: 2,
                      color: '#444',
                      fontSize: '1rem',
                      transform: 'rotate(-1deg)'
                    }}
                  >
                    Christmas 2025
                  </Typography>

                  {/* Delete Button (Pinned to top right of the photo) */}
                  {onPhotoDeleted && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation(); // Prevent clicking the card itself
                        handleDelete(photo.id);
                      }}
                      sx={{
                        position: 'absolute',
                        top: -10,
                        right: -10,
                        backgroundColor: '#ff4444',
                        color: 'white',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                        '&:hover': { backgroundColor: '#cc0000' }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                {/* --- END POLAROID --- */}

              </Grid>
            ))}
          </Grid>
        )}
      </Box>
    </Modal>
  );
}

export default PhotoGallery;